import React, { useEffect, useState } from 'react';
import { IconButton } from '@material-ui/core';
import { Create as EditIcon } from '@material-ui/icons';
import Table, {
  columnType,
  tempFilteredRows,
} from '../../../components/ClearingTable/Table';
import Button from '../../../components/Buttons/Button';
import AutoCompleteAccountNo from '../../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteSymbol from '../../../components/AutoComplete/AutoCompleteSymbol';
import AutoCompleteCorrespondent from '../../../components/AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteMasterAccountNo from '../../../components/AutoComplete/AutoCompleteMasterAccountNo';
import SelectSystemCode from '../../../components/Dropdown/SelectSystemCode';
import Text from '../../../components/Typography/Text';
import { protoTimeSpanObjectToString } from '../../../services/ConvertService';

import TextField from '../../../components/Textfields/TextField';
import DateTimePicker from '../../../components/Textfields/DateTimePicker';
import MoneyField from '../../../components/Textfields/MoneyField';
import DigitalCashRequestModal from './Components/DigitalCashRequestModal';
import DigitalCashRequestSummaryModal from './Components/DigitalCashRequestSummaryModal';
import {
  notifyInfo,
  notifyError,
  notifySuccess,
} from '../../../components/Messages/Notification';
import {
  listDigitalCashRequest,
  createDigitalCashRequest,
  updateDigitalCashRequest,
} from '../../../services/DigitalCashRequestService';
import { getSystemDate } from '../../../services/ProfileService';
import QueryParam from '../../../services/QueryParamService';
import {
  createRecurrence,
  updateRecurrence,
  deleteRecurrence,
} from '../../../services/RecurrenceService';

import { getProfileName } from '../../../services/ProfileService';
import { getCurrentUser, getAccess } from '../../../services/AuthService';
import {
  getAccountInfo,
  getAccessibleClient,
} from '../../../services/ClientAccountService';

import {
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderAlign,
} from '../../../components/Table/CustomBodyRender';

export default function DigitalCashRequest() {
  const [rows, setRows] = React.useState([]);
  const [rowData, setRowData] = React.useState({});

  const [open, setOpen] = React.useState({
    add: false,
    summary: false,
  });

  const [loading, setLoading] = React.useState({
    search: false,
    save: false,
  });

  const [profileName, setProfileName] = React.useState('');
  const [defaultAccount, setDefaultAccount] = React.useState([]);
  const [defaultWallet, setDefaultWallet] = React.useState([]);
  const [addLoading, setAddLoading] = React.useState(true);
  const [lockTable, setLockTable] = useState(true);
  const [searchDataCsv, setSearchDataCsv] = React.useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [previousRows, setPreviousRows] = useState(0);
  const [pagination, setPagination] = useState({});

  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      fromDate: '',
      toDate: '',
      correspondent: '',
      accountNo: '',
      symbol: '',
      externalId: '',
      masterAccountNo: '',
      transferType: '',
      requestType: '',
      status: '',
      sign: '',
      amount: '',
      search: '',
    })
  );

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    let account_no = '';
    let correspondent = '';
    try {
      const systemDate = await getSystemDate();

      const serverProfile = await getProfileName();
      const loggeduser = getCurrentUser();
      setProfileName(serverProfile.toLowerCase());
      if (loggeduser.AccountId !== 0) {
        const account = await getAccountInfo();
        setDefaultAccount(account);
        account_no = account.accountNo;
        correspondent = account.correspondent;
      } else {
        const account = await getAccessibleClient(loggeduser.UserId);
        setDefaultAccount(account);
        account_no = account.client.accountNo;
        correspondent = account.client.correspondent;
      }
      if (!getAccess('account_no', loggeduser.UserType)) {
        setSearchData({
          ...searchData,
          fromDate: searchData.fromDate ? searchData.fromDate : systemDate,
          toDate: searchData.toDate ? searchData.toDate : systemDate,
          accountNo: account_no,
          correspondent: correspondent,
        });
      } else {
        setSearchData({
          ...searchData,
          fromDate: searchData.fromDate ? searchData.fromDate : systemDate,
          toDate: searchData.toDate ? searchData.toDate : systemDate,
        });
      }
    } catch (error) {
      notifyError(error.message);
    }

    if (searchData.search === true) {
      handleSearch();
    }
    setAddLoading(false);
  };

  const columns = [
    {
      name: 'actions',
      label: 'Actions',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <div>
              <div style={{ padding: 5 }}>
                <IconButton
                  aria-label="edit"
                  onClick={() => handleOpen(tempFilteredRows[dataIndex])}
                >
                  <EditIcon style={{ height: 24, width: 24 }} />
                </IconButton>
              </div>
            </div>
          );
        },
        sort: false,
      },
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountName',
      label: 'Account Name',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'broker',
      label: 'Broker',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'type',
      label: 'Type',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'symbol',
      label: 'Symbol',
    },
    {
      name: 'amt',
      label: 'Amount',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: 'amount',
    },
    {
      name: 'transferType',
      label: 'Transfer Type',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'referenceId',
      label: 'Reference ID',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'confirmationId',
      label: 'Confirmation ID',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'publicDescription',
      label: 'Public Description',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'privateDescription',
      label: 'Private Description',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'internalId',
      label: 'Internal ID',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'externalId',
      label: 'External ID',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'bankId',
      label: 'DDA Bank',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'requestType',
      label: 'DDA Request Type',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'createdBy',
      label: 'Created By',
      options: {
        display: false,
      },
    },
    {
      name: 'createdAt',
      label: 'Created At',
      options: {
        display: false,
        customBodyRenderLite: (dataIndex) => {
          return protoTimeSpanObjectToString(
            rows[dataIndex]?.createdAt,
            'MM/DD/YYYY hh:mm:ss a'
          );
        },
      },
    },
    {
      name: 'modifiedAt',
      label: 'Modified At',
      options: {
        display: false,
        customBodyRenderLite: (dataIndex) => {
          return protoTimeSpanObjectToString(
            rows[dataIndex]?.modifiedAt,
            'MM/DD/YYYY hh:mm:ss a'
          );
        },
      },
    },
    {
      name: 'modifiedBy',
      label: 'Modified By',
      options: {
        display: false,
      },
    },
    {
      name: 'systemDate',
      label: 'System Date',
      type: columnType.date,
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    serverSide: true,
  };

  const handleSearchDataChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    if (input.name === 'sign' && input.value === '') {
      searchData.amount = '';
    }

    setSearchData({ ...searchData, [input.name]: input.value });
  };

  const getCsvData = async () => {
    if (lockTable === true) {
      return [];
    }
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: totalRows,
    };

    const data = await listDigitalCashRequest(searchDataCsv, paginationCopy);

    const rowsCopy = data.dcRequestsList.map((item) => {
      item.createdAt = item.createdAt
        ? protoTimeSpanObjectToString(item.createdAt, 'MM/DD/YYYY hh:mm:ss a')
        : '--';
      item.modifiedAt = item.modifiedAt
        ? protoTimeSpanObjectToString(item.modifiedAt, 'MM/DD/YYYY hh:mm:ss a')
        : '--';

      return item;
    });

    return rowsCopy;
  };

  const handleSearch = async (paginationChange) => {
    try {
      if (paginationChange !== true) {
        setLoading({ ...loading, search: true });
        setLockTable(false);
      }
      if (paginationChange === true) {
        if (lockTable === true) {
          return;
        }
      }
      QueryParam.set(searchData);
      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };
      if (previousRows != 0) {
        if (previousRows < pagination.rowsPerPage * pagination.pageNo) {
          paginationCopy.pageNo = 0;
        }
      }
      if (paginationChange !== true) {
        paginationCopy.filterNames = [];
        paginationCopy.filterValues = [];
      }
      const data = await listDigitalCashRequest(searchData, paginationCopy);
      setSearchDataCsv(searchData);
      setTotalRows(data.summary.totalRows);
      setPreviousRows(totalRows);
      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);
      const processedRows = data.dcRequestsList.map((data) => ({
        dcRequestId: data.dcRequestId,
        correspondent: data.correspondent,
        accountNo: data.accountNo,
        accountName: data.accountName,
        masterAccountNo: data.masterAccountNo,
        broker: data.broker,
        type: data.type,
        symbol: data.symbol,
        amt: data.amt,
        bankId: data.bankId,
        walletId: data.walletId,
        requestType: data.requestType,
        transferType: data.transferType,
        referenceId: data.referenceId,
        confirmationId: data.confirmationId,
        status: data.status,
        publicDescription: data.publicDescription,
        privateDescription: data.privateDescription,
        internalId: data.internalId,
        externalId: data.externalId,
        createdBy: data.createdBy,
        modifiedBy: data.modifiedBy,
        systemDate: data.systemDate,
        createdAt: data.createdAt,
        modifiedAt: data.modifiedAt,
      }));
      setRows(processedRows);
      // setRows(data.dcRequestsList);
      if (paginationChange !== true) {
        notifyInfo(data.summary.totalRows + ' search results.');
      }
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoading({ ...loading, search: false });
    }
  };

  const handleOpen = (data) => {
    let rowData = data;
    const correspondent = defaultAccount?.client?.correspondent
      ? defaultAccount.client.correspondent
      : '';
    const account_no = defaultAccount?.client?.accountNo
      ? defaultAccount.client.accountNo
      : '';
    const wallet_id = defaultWallet.length > 0 ? defaultWallet[0].walletId : '';

    if (!rowData) {
      rowData = {
        correspondent: correspondent,
        accountNo: account_no,
        transferType: '',
        walletId: wallet_id,
        contraWalletId: '',
        amt: '',
        status: 'Pending',
        referenceId: '',
        confirmationId: '',
        internalId: '',
        externalId: '',
        privateDescription: '',
        publicDescription: '',
      };
    }

    setRowData(rowData);
    setOpen({ ...open, add: true });
  };

  const handleClose = async (data, isEdit, recData) => {
    if (!data) {
      setOpen({ ...open, add: false });
      return;
    }

    let valid = true;

    if (!data.correspondent) {
      notifyError('Correspondent is required.');
      valid = false;
    }

    if (!data.accountNo) {
      notifyError('Account No is required.');
      valid = false;
    }

    if (!data.transferType) {
      notifyError('Transfer Type is required.');
      valid = false;
    }

    // if (!data.walletId) {
    //   notifyError('Wallet is required.')
    //   valid = false
    // }

    if (!data.amt) {
      notifyError('Amount is required.');
      valid = false;
    }

    if (!data.status) {
      notifyError('Status is required.');
      valid = false;
    }

    if (data.transferType !== 'Transfer Send') {
      if (!data.bankId) {
        notifyError('DDA Bank is required.');
        valid = false;
      }
      if (!data.requestType) {
        notifyError('DDA Request Type is required.');
        valid = false;
      }
    }

    if (!valid) {
      return;
    }

    try {
      setLoading({ ...loading, save: true });
      let resp;
      if (isEdit) {
        resp = await updateDigitalCashRequest(data);
        const rowsCopy = [...rows];
        const index = rows.indexOf(rowData);

        rowsCopy[index] = resp.dcRequest;

        setRows(rowsCopy);
        notifySuccess('Digital cash request has been updated.');
      } else {
        resp = await createDigitalCashRequest(data);

        setRows([resp.dcRequest, ...rows]);
        notifySuccess('New Digital cash request has been added.');
      }
      if (recData && data.transferType === 'Transfer Send' && recData.linkId !== 0) {
        if (recData.recurrenceId !== 0) {
          if (recData.recurring === 'None') {
            await deleteRecurrence(recData);
          } else {
            await updateRecurrence(recData);
          }
        } else {
          await createRecurrence(recData);
        }
      }
    } catch (error) {
      setLoading({ ...loading, save: false });
      notifyError(error.message);
    } finally {
      setOpen({ ...open, add: false });
      setLoading({ ...loading, save: false });
    }
  };

  const handleOpenSummary = () => {
    setOpen({ ...open, summary: true });
  };

  const handleCloseSummary = () => {
    setOpen({ ...open, summary: false });
  };

  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Digital Cash Request" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading.search}
              loading={loading.search}
              type="search"
              label={loading.search ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
          <div className="grd-cell-none">
            <Button label="Summary" onClick={() => handleOpenSummary()} />
          </div>
          <div className="grd-cell-none">
            <Button
              type="plus"
              label="Add Digital Cash Request"
              loading={addLoading}
              disabled={addLoading}
              onClick={() => handleOpen()}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <DateTimePicker
              required
              type="date"
              name="fromDate"
              label="From Date"
              value={searchData.fromDate}
              onChange={handleSearchDataChange}
              inputProps={{ max: searchData.toDate }}
            />
          </div>
          <div className="grd-cell">
            <DateTimePicker
              required
              type="date"
              name="toDate"
              label="To Date"
              value={searchData.toDate}
              onChange={handleSearchDataChange}
              inputProps={{ min: searchData.fromDate }}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              name="correspondent"
              label={'Correspondent'}
              value={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountNo
              name="accountNo"
              value={searchData.accountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteMasterAccountNo
              name="masterAccountNo"
              value={searchData.masterAccountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <AutoCompleteSymbol
              name="symbol"
              value={searchData.symbol}
              onChange={handleSearchDataChange}
              assetType="DA"
              isActive
            />
          </div>
          <div className="grd-cell">
            <TextField
              name="externalId"
              label="External ID"
              placeholder="External ID"
              type="text"
              value={searchData.externalId}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="transferType"
              label="Transfer Type"
              placeholder="Transfer Type"
              type="Client Site"
              subType="Transfer Type"
              value={searchData.transferType}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="status"
              label="Status"
              placeholder="Status"
              type="Status"
              subType="Bank Request"
              value={searchData.status}
              onChange={handleSearchDataChange}
              withBankStatusGroup={true}
            />
          </div>
          <div className="grd-cell">
            <div className="grd-row nm">
              <div className="grd-cell">
                <SelectSystemCode
                  name="sign"
                  label="Sign"
                  placeholder="Sign"
                  type="Sign"
                  subType="Bank Request"
                  value={searchData.sign}
                  onChange={handleSearchDataChange}
                />
              </div>
              <div className="grd-cell">
                <MoneyField
                  disabled={searchData.sign ? false : true}
                  name="amount"
                  label="Amount"
                  placeholder="$"
                  value={searchData.amount}
                  onChange={handleSearchDataChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-20">
        <Table
          title={'Digital Cash Request'}
          data={rows}
          columns={columns}
          options={options}
          onSearch={handleSearch}
          pagination={pagination}
          setPagination={setPagination}
          getCsvData={getCsvData}
        />
      </div>

      <DigitalCashRequestModal
        open={open.add}
        close={handleClose}
        data={rowData}
        loading={loading.save}
      />
      <DigitalCashRequestSummaryModal
        open={open.summary}
        close={handleCloseSummary}
        data={searchData}
      />
    </form>
  );
}
